import { Link } from 'react-router-dom';
import { formatCalendarDate } from '@resida/shared/timezone';
import { GUIDES, GUIDE_CATEGORY_LABELS } from '../content/guides';
import { CTABanner } from '../components/marketing/CTABanner';
import { Breadcrumb } from '../components/marketing/Breadcrumb';
import { SEO } from '../components/SEO';

export function GuidesIndexPage(): JSX.Element {
  return (
    <>
      <SEO
        title="Landlord Guides"
        description="Practical how-tos, checklists, and explainers for independent landlords: rent collection, leases, maintenance, tenant screening, and tax-time records."
        path="/guides"
      />

      <section className="pt-12 pb-16 bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Guides' }]} />
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--landing-text-primary)] leading-tight mb-4">
              Guides for independent landlords
            </h1>
            <p className="text-lg text-[var(--landing-text-muted)]">
              Plain-language answers to the questions that come up when you manage your own rentals.
            </p>
          </div>
        </div>
      </section>

      <section className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <ul data-testid="guides-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {GUIDES.map((guide) => (
              <li key={guide.slug}>
                <Link
                  to={`/guides/${guide.slug}`}
                  className="h-full bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-6 border border-[var(--landing-border)] hover:border-[var(--landing-accent)] transition-colors group flex flex-col"
                >
                  <span className="text-xs font-medium uppercase tracking-widest text-[var(--landing-accent-text)] mb-2">
                    {GUIDE_CATEGORY_LABELS[guide.category]}
                  </span>
                  <h2 className="text-lg font-semibold text-[var(--landing-text-primary)] group-hover:text-[var(--landing-accent-text)] transition-colors mb-2">
                    {guide.title}
                  </h2>
                  <p className="text-sm text-[var(--landing-text-muted)] leading-relaxed mb-4 flex-1">
                    {guide.description}
                  </p>
                  <time
                    dateTime={guide.updatedAt ?? guide.publishedAt}
                    className="text-xs text-[var(--landing-text-muted)]"
                  >
                    {formatCalendarDate(guide.updatedAt ?? guide.publishedAt)}
                  </time>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
